import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import MainLayout from "../../layouts/Layout";
import { SearchOutlined, BellOutlined } from "@ant-design/icons";
import { useSecureService } from "../../store/service";
import { List, Spin, Checkbox, Button } from "antd";
import { useAtom } from "jotai";
import { userAtom } from "../../store/atoms";
import SearchResultItem from "../../components/SearchResultItem/SearchResultItem";
import EntryResultItem from "../../components/EntryResultItem/EntryResultItem";
import NotificationResultItem from "../../components/NotificationResultItem/NotificationResultItem";
import styles from "./Results.module.scss";
import NoDataComponent from "../../components/NoDataComponent/NoDataComponent";

interface ResultsProps {
  type?: 'search' | 'notifications' | 'entry';
  query?: string;
}

const Results: React.FC<ResultsProps> = ({ type = 'search', query = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userState, setUserState] = useAtom(userAtom);
  const {  getUserNotifications, changeNotificationStatus, getEntrySummary } = useSecureService();
  const [loading, setLoading] = useState(true);
  const [markAsReadLoading, setMarkAsReadLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [entryLoading, setEntryLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState(query);
  const [pageType, setPageType] = useState(type);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // URL'den parametreleri al
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlType = searchParams.get('type') as 'search' | 'notifications' | 'entry' || 'search';
    const urlQuery = searchParams.get('q') || '';
    const urlStatus = searchParams.get('status') || '';
    const urlStartDate = searchParams.get('startDate') || '';
    const urlScope = searchParams.get('scope') as 'Internal' | 'External' | null;
    setPageType(urlType);
    setSearchQuery(urlQuery);
    // Entry tipi için veri yükle (scope: Internal | External)
    if (urlType === 'entry') {
      loadEntryResults(urlStatus, urlStartDate, urlScope || undefined);
    }
  }, [location.search]);

  // Search results için
  const loadSearchResults = async () => {
    if (!searchQuery.trim()) {
      setResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      // TODO: Search API'si implement edildiğinde buraya eklenecek
      setResults([]);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Entry results için (scope: Internal | External)
  const loadEntryResults = async (status?: string, startDate?: string, scope?: 'Internal' | 'External') => {
    setEntryLoading(true);
    try {
      const response = await getEntrySummary(status, startDate, scope);
      if (response.success && response.data?.items) {
        setResults(response.data.items);
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error('Entry results error:', error);
      setResults([]);
    } finally {
      setEntryLoading(false);
    }
  };

  // Notifications için
  const loadNotifications = async () => {
    setNotificationLoading(true);
    try {
      const response = await getUserNotifications();
      if (response.success && response.data?.notifications) {
        setResults(response.data.notifications);
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error('Notifications error:', error);
      setResults([]);
    } finally {
      setNotificationLoading(false);
    }
  };

  useEffect(() => {
    if (pageType === 'search') {
      loadSearchResults();
    } else if (pageType === 'notifications') {
      loadNotifications();
    }
  }, [pageType, searchQuery]);

  // Ana loading state'ini güncelle
  useEffect(() => {
    setLoading(searchLoading || entryLoading || notificationLoading);
  }, [searchLoading, entryLoading, notificationLoading]);

  const handleItemClick = (item: any) => {
    if (pageType === 'notifications' && item.notificationTarget) {
      navigate(`/${item.notificationTarget}`);
    } else if (pageType === 'search') {
      navigate(`/${item.path}`);
    } else if (pageType === 'entry') {
      navigate(`/${item.path}`);
    }
  };

  const handleEntryNameClick = (target: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(`/${target}`);
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedItems(results.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleItemSelect = (itemId: number, checked: boolean) => {
    if (checked) {
      setSelectedItems(prev => [...prev, itemId]);
    } else {
      setSelectedItems(prev => prev.filter(id => id !== itemId));
    }
  };

  const handleMarkAsRead = async () => {
    if (selectedItems.length === 0) return;
    
    setMarkAsReadLoading(true);
    try {
      const response = await changeNotificationStatus(selectedItems, true);
      if (response.success) {
        // Seçili öğeleri listeden kaldır ve listeyi yenile
        setSelectedItems([]);
        setSelectAll(false);
        // Bildirimleri yeniden yükle
        await loadNotifications();
        // Header'daki badge'i güncellemek için user state'ini manuel güncelle
        if (userState) {
          setUserState({
            ...userState,
            unreadNotificationCount: Math.max(0, (userState.unreadNotificationCount || 0) - selectedItems.length)
          });
        }
      } else {
        console.error('Error marking as read:', response.message);
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    } finally {
      setMarkAsReadLoading(false);
    }
  };

  const getPageTitle = () => {
    if (pageType === 'search') {
      return searchQuery ? `"${searchQuery}" için arama sonuçları` : 'Arama Sonuçları';
    } else if (pageType === 'entry') {
      // URL'den direkt oku, pageType state'ine güvenme
      const searchParams = new URLSearchParams(location.search);
      const status = searchParams.get('status');

      if (status) {
        return `${status}`;
      }
      return 'All Projects';
    } else {
      return 'All Notifications';
    }
  };

  const getPageIcon = () => {
    if (pageType === 'search') {
      return <SearchOutlined />;
    } else if (pageType === 'entry') {
      return <SearchOutlined />;
    } else {
      return <BellOutlined />;
    }
  };
  
  return (
    <MainLayout title={getPageTitle()} icon={getPageIcon()}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px', color: '#999' }}>
            {searchLoading ? 'Arama yapılıyor...' : 
             entryLoading ? 'Projeler yükleniyor...' : 
             notificationLoading ? 'Bildirimler yükleniyor...' : 
             'Yükleniyor...'}
          </div>
        </div>
      ) : results.length > 0 ? (
        <>
          {pageType === 'notifications' && results.some((item: any) => !item.isRead) && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '16px 0',
              borderBottom: '1px solid var(--app-divider-color)',
              marginBottom: '16px'
            }}>
              <Checkbox
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
              >
                Select All
              </Checkbox>
              <Button
                type="primary"
                onClick={handleMarkAsRead}
                disabled={selectedItems.length === 0 || markAsReadLoading}
                style={{ 
                  backgroundColor: 'var(--app-accent-color)', 
                  borderColor: 'var(--app-accent-color)',
                  color: '#fff'
                }}
                className={selectedItems.length === 0 || markAsReadLoading ? styles.disabledButton : ''}
              >
                {markAsReadLoading ? 'Marking...' : 'Mark All as Read'}
              </Button>
            </div>
          )}
          {searchLoading && pageType === 'search' ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: '#999' }}>Arama sonuçları yükleniyor...</div>
            </div>
          ) : entryLoading && pageType === 'entry' ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: '#999' }}>Projeler yükleniyor...</div>
            </div>
          ) : notificationLoading && pageType === 'notifications' ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: '#999' }}>Bildirimler yükleniyor...</div>
            </div>
          ) : (
            <List
              className={styles.resultsList}
              dataSource={results}
              renderItem={(item) => {
                if (pageType === 'search') {
                  return <SearchResultItem item={item} onItemClick={handleItemClick} />;
                } else if (pageType === 'entry') {
                  return <EntryResultItem item={item} onItemClick={handleItemClick} />;
                } else {
                  return (
                    <NotificationResultItem 
                      item={item} 
                      isSelected={selectedItems.includes(item.id)}
                      onItemSelect={handleItemSelect}
                      onEntryNameClick={handleEntryNameClick}
                    />
                  );
                }
              }}
            />
          )}
        </>
      ) : (
        <NoDataComponent message="No Data Found!" />
      )}
    </MainLayout>
  );
};

export default Results; 